import { useState, useEffect } from "react";
import { Plus, Edit, DollarSign, Image as ImageIcon
} from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

import { SharetribeRepository } from "../infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";

export function YourListingsContent() {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const [listings, setListings] = useState<any[]>([]);
  const [fetchingListings, setFetchingListings] = useState(false);

  const [editingListingId, setEditingListingId] = useState<string | null>(null);
  const [messageDialog, setMessageDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({
    open: false,
    title: "",
    message: ""
  });

  const repo = new SharetribeRepository(apiService);

  const [newListing, setNewListing] = useState<{
    name: string;
    description: string;
    price: string;
    photo: File | string | null;
    existingImageId: string;
  }>({
    name: "",
    description: "",
    price: "",
    photo: null,
    existingImageId: ""
  });

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // -----------------------------------------
  // LOAD LISTINGS FROM SHARETRIBE
  // -----------------------------------------
  const loadListings = async () => {
    try {
      setFetchingListings(true);

      const response = await repo.ownListingsQuery({}, ["images"]);

      if (!response || !response.data) {
        setListings([]);
        return;
      }

      const data = response.data;
      const included = response.included || [];

      const filtered = data.filter(
        (item: any) => item?.attributes?.publicData?.listingType === "product"
      );

      const mapped = filtered.map((item: any) => {
        const attr = item.attributes;
        const id = item.id?.uuid || item.id;

        const price = attr.price?.amount ? attr.price.amount / 100 : 0;

        let imageUrl = "/placeholder-avatar.jpg";

        const imageRel = item.relationships?.images?.data?.[0];
        if (imageRel) {
          const image = included.find(
            (inc: any) => inc.id === imageRel.id && inc.type === "image"
          );

          if (image) {
            imageUrl = image.attributes?.variants?.default?.url || imageUrl;
          }
        }

        return {
          id,
          title: attr.title,
          description: attr.description,
          price,
          imageId: imageRel?.id || "",
          image: imageUrl,
          state: attr.state // <- published or closed
        };
      });

      setListings(mapped);
    } catch (err) {
      console.error("Load Listings Error:", err);
    }

    setFetchingListings(false);
  };

  useEffect(() => {
    loadListings();
  }, []);

  // -----------------------------------------
  // UPLOAD PHOTO
  // -----------------------------------------
  const handlePhotoUpload = (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPhotoPreview(url);

    setNewListing((prev) => ({ ...prev, photo: file }));
  };

  // RESET FORM
  const resetForm = () => {
    setEditingListingId(null);
    setNewListing({
      name: "",
      description: "",
      price: "",
      photo: null,
      existingImageId: ""
    });
    setPhotoPreview(null);
  };

 const handlePublish = async () => {
  if (!newListing.name || !newListing.description || !newListing.price) {
    setMessageDialog({
      open: true,
      title: "Missing Fields",
      message: "Please fill all fields before publishing."
    });
    return;
  }

  try {
    setLoading(true);

    let imageId: string | null = null;

    if (newListing.photo) {
      const imgRes = await repo.uploadImage(newListing.photo, "image/jpeg");
      if (!imgRes?.data?.id) {
        setMessageDialog({
          open: true,
          title: "Upload Failed",
          message: "Image upload failed. Please try again."
        });
        return;
      }
      imageId = imgRes.data.id;
    }

    const priceAmount = Math.floor(Number(newListing.price) * 100);
    const publicData = { listingType: "product" };
    const imagesArr = imageId ? [imageId] : [];

    await repo.ownListingsCreate(
      newListing.name.trim(),
      newListing.description.trim(),
      0, 0,
      "USD",
      priceAmount,
      imagesArr,
      null, null,
      publicData,
      []
    );

    setMessageDialog({
      open: true,
      title: "Success",
      message: "Product created successfully!"
    });

    resetForm();
    setOpenDialog(false);
    loadListings();

  } catch (err) {
    setMessageDialog({
      open: true,
      title: "Error",
      message: "Something went wrong. Please try again."
    });
  }

  setLoading(false);
};

const handleUpdate = async () => {
  if (!editingListingId) return;

  try {
    setLoading(true);

    let imageId: string | null = newListing.existingImageId || null;

    if (newListing.photo && newListing.photo instanceof File) {
      const imgRes = await repo.uploadImage(newListing.photo, "image/jpeg");
      if (!imgRes?.data?.id) {
        setMessageDialog({
          open: true,
          title: "Upload Failed",
          message: "Image upload failed. Please try again."
        });
        return;
      }
      imageId = imgRes.data.id;
    }

    const priceAmount = Math.floor(Number(newListing.price) * 100);
    const publicData = { listingType: "product" };
    const imagesArr = imageId ? [imageId] : [];

    await repo.ownListingsUpdate(
      editingListingId,
      newListing.name.trim(),
      newListing.description.trim(),
      0, 0,
      "USD",
      priceAmount,
      imagesArr,
      null, null,
      publicData,
      []
    );

    setMessageDialog({
      open: true,
      title: "Updated",
      message: "Product updated successfully!"
    });

    resetForm();
    setOpenDialog(false);
    loadListings();

  } catch (err) {
    setMessageDialog({
      open: true,
      title: "Error",
      message: "Something went wrong during update."
    });
  }

  setLoading(false);
};

const handleCloseListing = async (listingId: string) => {
  try {
    await repo.ownListingsClose(listingId);

    setMessageDialog({
      open: true,
      title: "Listing Closed",
      message: "Your product is now hidden from customers."
    });

    loadListings();
  } catch {
    setMessageDialog({
      open: true,
      title: "Failed",
      message: "Unable to close listing."
    });
  }
};

const handleOpenListing = async (listingId: string) => {
  try {
    await repo.ownListingsOpen(listingId);

    setMessageDialog({
      open: true,
      title: "Listing Opened",
      message: "Your product is now visible to customers."
    });

    loadListings();
  } catch {
    setMessageDialog({
      open: true,
      title: "Failed",
      message: "Unable to open listing."
    });
  }
};


  // -----------------------------------------
  // START EDITING
  // -----------------------------------------
  const startEditing = (listing: any) => {
    setEditingListingId(listing.id);
    setNewListing({
      name: listing.title,
      description: listing.description,
      price: String(listing.price),
      photo: null,
      existingImageId: listing.imageId
    });
    setPhotoPreview(listing.image);
    setOpenDialog(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Your Listings</h1>
          <p className="text-muted-foreground">Manage your product listings</p>
        </div>

        <Button onClick={() => { resetForm(); setOpenDialog(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          Create New Listing
        </Button>
      </div>

      {/* Create/Edit Dialog */}
     <Dialog open={openDialog} onOpenChange={setOpenDialog}>
  <DialogContent className="max-w-md">
    <DialogHeader>
      <DialogTitle>
        {editingListingId ? "Edit Product Listing" : "New Product Listing"}
      </DialogTitle>
    </DialogHeader>

    <div className="space-y-5">

      <div className="space-y-1.5">
        <Label>Product Name</Label>
        <Input
          value={newListing.name}
          onChange={(e) =>
            setNewListing({ ...newListing, name: e.target.value })
          }
          className="break-all overflow-hidden text-ellipsis"
          required
        />
      </div>

      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea
          value={newListing.description}
          onChange={(e) =>
            setNewListing({ ...newListing, description: e.target.value })
          }
          className="break-words whitespace-pre-wrap"
          required
        />
      </div>

      <div className="space-y-1.5">
        <Label>Price</Label>
        <Input
          type="number"
          value={newListing.price}
          onChange={(e) =>
            setNewListing({ ...newListing, price: e.target.value })
          }
        />
      </div>

      <div className="space-y-1.5">
        <Label>Photo</Label>
        <Input type="file" accept="image/*" onChange={handlePhotoUpload} />

        {photoPreview ? (
          <img
            src={photoPreview}
            className="h-24 w-24 rounded-md mt-3 object-cover"
          />
        ) : (
          <div className="h-24 w-24 bg-muted rounded-md flex items-center justify-center mt-3">
            <ImageIcon />
          </div>
        )}
      </div>

    </div>

    <DialogFooter>
      <Button
        className="w-full"
        disabled={loading}
        onClick={editingListingId ? handleUpdate : handlePublish}
      >
        {loading
          ? "Processing..."
          : editingListingId
          ? "Update Listing"
          : "Publish"}
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>


      {/* Listings */}
      <div className="space-y-4">
        {fetchingListings ? (
          <p>Loading listings...</p>
        ) : listings.length === 0 ? (
          <p>No products found.</p>
        ) : (
          listings.map((listing) => (
            <Card key={listing.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-4">

                  <Avatar className="h-16 w-16">
                    <AvatarImage src={listing.image} />
                    <AvatarFallback>IMG</AvatarFallback>
                  </Avatar>

                  <div className="flex-1">
                    <div className="flex justify-between">
                      <div>
                          <h3
                              className="font-semibold text-lg max-w-full break-all whitespace-pre-wrap"
                            >
                              {listing.title}
                            </h3>

                            <p
                              className="text-muted-foreground mb-2 max-w-full break-all whitespace-pre-wrap"
                            >
                              {listing.description}
                            </p>

                        </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => startEditing(listing)}>
                          <Edit className="h-4 w-4" />
                        </Button>

                        {listing.state === "published" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleCloseListing(listing.id)}
                          >
                            Close
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600"
                            onClick={() => handleOpenListing(listing.id)}
                          >
                            Open
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 mt-2 text-sm">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span>{listing.price}</span>
                    </div>

                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}

{/* GLOBAL MESSAGE DIALOG */}
<Dialog open={messageDialog.open} onOpenChange={(v) => setMessageDialog({ ...messageDialog, open: v })}>
  <DialogContent className="max-w-sm">
    <DialogHeader>
      <DialogTitle>{messageDialog.title}</DialogTitle>
    </DialogHeader>
    <p className="text-muted-foreground">{messageDialog.message}</p>
    <DialogFooter>
      <Button onClick={() => setMessageDialog({ ...messageDialog, open: false })}>
        OK
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>


      </div>
    </div>



  );
}
